/**
 * MailTrackingPage — `/mail/track` staff-side tracking lookup.
 *
 * Ported from `screens/src/pages/mail/MailTracking.jsx`. Calls the public
 * tracking endpoint `GET /v1/track/:trackingNumber` (it's `@Public()` in
 * service-mail, so no bearer required). Staff context is preserved by living
 * inside StaffLayout. Differs from the customer-portal `/portal/track` only in
 * chrome and the deep-link CTA (staff get an "Open in items" link if the
 * tracking number resolves to one of their operator items).
 */
import { type FormEvent, type JSX, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { trackByNumber } from '../../lib/api/mail-items';
import { PageHeader, StatusBadge } from '../shared/StaffUI';

export function MailTrackingPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const tnParam = params.get('tn') ?? '';
  const [input, setInput] = useState<string>(tnParam);
  const [submitted, setSubmitted] = useState<string>(tnParam);

  const query = useQuery({
    queryKey: ['mail', 'track', submitted],
    queryFn: () => trackByNumber(submitted),
    enabled: submitted.length >= 6,
    retry: false,
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const trimmed = input.trim().toUpperCase();
    setSubmitted(trimmed);
    setParams(trimmed.length > 0 ? { tn: trimmed } : {}, { replace: false });
  }

  const notFound = query.isError && query.error instanceof ApiError && query.error.status === 404;

  return (
    <div>
      <PageHeader
        title="Track Mail"
        description="Lookup any registered item by its UPU S10 tracking number."
      />

      <form onSubmit={handleSubmit} className="bg-white border border-border rounded-xl p-5 mb-5">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. EE123456785NG"
              aria-label="Tracking number"
              autoComplete="off"
              spellCheck={false}
              className="w-full pl-9 pr-4 py-2.5 text-base font-mono uppercase border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
            />
          </div>
          <button
            type="submit"
            className="px-6 py-2.5 bg-nipost-green text-white font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            Track
          </button>
        </div>
      </form>

      {submitted.length === 0 ? (
        <p className="text-sm text-muted">Enter a tracking number to begin.</p>
      ) : query.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : notFound ? (
        <div className="bg-white border border-border rounded-xl p-6 text-center">
          <p className="text-sm font-semibold text-gray-700">No item found</p>
          <p className="text-xs text-muted mt-1">
            <span className="font-mono">{submitted}</span> didn&apos;t match any registered mail.
          </p>
        </div>
      ) : query.isError ? (
        <div className="bg-white border border-border rounded-xl p-6 text-center">
          <p className="text-sm text-red-700">{(query.error as Error).message}</p>
        </div>
      ) : query.data ? (
        <div className="bg-white border border-border rounded-xl p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold text-muted uppercase tracking-widest">Tracking</p>
              <p className="text-base font-bold font-mono text-gray-900">{query.data.trackingNumber}</p>
              <p className="text-xs text-muted mt-1">
                Service {query.data.serviceClass} · registered{' '}
                {new Date(query.data.registeredAt).toLocaleString()}
              </p>
            </div>
            <StatusBadge status={query.data.status} />
          </div>

          <div>
            <p className="text-xs font-semibold text-muted uppercase tracking-widest mb-2">Timeline</p>
            <ol className="space-y-2">
              {query.data.events.map((ev, idx) => (
                <li
                  key={`${ev.eventType}-${ev.recordedAt}-${idx}`}
                  className="flex items-center gap-3 text-sm"
                >
                  <span className="w-2 h-2 rounded-full bg-nipost-green flex-shrink-0" />
                  <span className="font-semibold text-gray-900">{ev.eventType}</span>
                  {ev.locationCode ? (
                    <span className="text-xs text-muted">@ {ev.locationCode}</span>
                  ) : null}
                  <span className="ml-auto text-xs text-muted">
                    {new Date(ev.recordedAt).toLocaleString()}
                  </span>
                </li>
              ))}
              {query.data.events.length === 0 ? (
                <li className="text-xs text-muted">No events recorded yet.</li>
              ) : null}
            </ol>
          </div>
        </div>
      ) : null}
    </div>
  );
}
